'use client';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Check, X } from 'lucide-react';
import { Avatar, Icon, Input } from '@ui/components';
import { IConversationMemberOption } from '../conversation.typings';

interface IProps {
  search: (term: string) => Promise<IConversationMemberOption[]>;
  selected: IConversationMemberOption[];
  onChange: (selected: IConversationMemberOption[]) => void;
  /** Members already in the message: listed, but they cannot be picked again. */
  excludedIds?: string[];
}

/**
 * Lets an admin find crew members by user name, name or email and pick several of them.
 */
export const CrewMemberPicker = ({ search, selected, onChange, excludedIds = [] }: IProps) => {
  const [term, setTerm] = useState('');
  const [debouncedTerm] = useDebounce(term.trim(), 300);
  const [results, setResults] = useState<IConversationMemberOption[]>([]);
  const [isSearching, setIsSearching] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);
  // Only the newest search may fill the list; an older one can answer after it.
  const latestSearch = useRef(0);

  useEffect(() => {
    const searchId = ++latestSearch.current;
    setIsSearching(true);
    setHasFailed(false);
    search(debouncedTerm)
      .then((members) => {
        if (searchId === latestSearch.current) setResults(members);
      })
      .catch(() => {
        if (searchId !== latestSearch.current) return;
        setResults([]);
        setHasFailed(true);
      })
      .finally(() => {
        if (searchId === latestSearch.current) setIsSearching(false);
      });
  }, [debouncedTerm, search]);

  const selectedIds = selected.map((member) => member.fan_id);

  const toggle = (member: IConversationMemberOption) =>
    onChange(
      selectedIds.includes(member.fan_id)
        ? selected.filter((picked) => picked.fan_id !== member.fan_id)
        : [...selected, member],
    );

  return (
    <div className="flex flex-col gap-3">
      {selected.length > 0 && (
        <ul aria-label="Selected members" className="flex flex-wrap gap-2">
          {selected.map((member) => (
            <li
              key={member.fan_id}
              className="flex items-center gap-1 rounded-full bg-input py-1 pl-3 pr-1 text-sm">
              {member.display_name}
              <button
                type="button"
                aria-label={`Remove ${member.display_name}`}
                className="rounded-full p-1 hover:bg-gray-200"
                onClick={() => toggle(member)}>
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Input
        value={term}
        placeholder="Search members by user name or email"
        onChange={(e) => setTerm(e.target.value)}
      />

      <div className="h-[240px] overflow-y-auto">
        {isSearching && !results.length ? (
          <div className="flex h-full items-center justify-center">
            <Icon type="loadingSpinner" className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : hasFailed ? (
          <p className="py-6 text-center text-sm text-gray-500">
            Couldn&apos;t load members. Try again.
          </p>
        ) : !results.length ? (
          <p className="py-6 text-center text-sm text-gray-500">
            {debouncedTerm ? `No members match "${debouncedTerm}"` : 'This crew has no members yet'}
          </p>
        ) : (
          <ul aria-label="Crew members" className="space-y-1">
            {results.map((member) => {
              const isAlreadyIn = excludedIds.includes(member.fan_id);
              const isPicked = selectedIds.includes(member.fan_id);
              return (
                <li key={member.fan_id}>
                  <button
                    type="button"
                    disabled={isAlreadyIn}
                    aria-pressed={isPicked}
                    onClick={() => toggle(member)}
                    className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-gray-50 disabled:cursor-default disabled:opacity-60">
                    <Avatar size="s" src={member.avatar_url} displayName={member.display_name} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-gray-900">
                        {member.display_name}
                      </span>
                      {member.email && (
                        <span className="block truncate text-sm text-gray-500">{member.email}</span>
                      )}
                    </span>
                    {isAlreadyIn ? (
                      <span className="text-xs text-gray-500">Already in this message</span>
                    ) : (
                      isPicked && <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
